import { IErrMsg } from '../../components/inputs/types'
import { IntegerSchemaValues } from '../../validators/inputValidators/utils/getIntegerSchema'
import Decimal from 'decimal.js'
import { normalizeDecimal } from '../../validators/inputValidators/utils/getNumberSchema'

export const numberInputErrors = (
    state: any,
    schema: any
): IErrMsg | undefined => {
    if (schema.type === 'integer') {
        return integerErrors(state, schema)
    }
    if (schema.type === 'double' || schema.type === 'duration') {
        return doubleErrors(state, schema)
    }
    if (schema.type === 'number') {
        return numberErrors(state, schema)
    }
}

const numberErrors = (state: string, schema: any): IErrMsg | undefined => {
    if (!schema.is_required && (state === null || state === undefined)) {
        return
    }
    if (state && !String(state).match(/^(-)?\d+(\.\d+)?$/)) {
        return { message: 'errors.validation.invalidValue' }
    }
    if (schema.is_required && !state) {
        return { message: 'errors.validation.validValueRequired' }
    }
    if (
        String(state).startsWith('-') &&
        String(state).split('.').join()?.length > 30
    ) {
        return { message: 'errors.validation.invalidValue' }
    }
    if (String(state).split('.')[1]?.length > 28) {
        return { message: 'errors.validation.invalidValue' }
    }
    if (
        typeof schema.greater_or_equal !== 'undefined' &&
        !new Decimal(normalizeDecimal(state)).gte(
            new Decimal(schema.greater_or_equal)
        )
    ) {
        return {
            message: 'errors.validation.greaterOrEqual',
            values: { greaterOrEqual: schema.greater_or_equal },
        }
    }
    if (
        typeof schema.greater_than !== 'undefined' &&
        !new Decimal(normalizeDecimal(state)).gt(
            new Decimal(schema.greater_than)
        )
    ) {
        return {
            message: 'errors.validation.greaterThan',
            values: { greaterThan: schema.greater_than },
        }
    }
    if (
        typeof schema.less_or_equal !== 'undefined' &&
        !new Decimal(normalizeDecimal(state)).lte(
            new Decimal(schema.less_or_equal)
        )
    ) {
        return {
            message: 'errors.validation.lessOrEqual',
            values: { lessOrEqual: schema.less_or_equal },
        }
    }
    if (
        typeof schema.less_than !== 'undefined' &&
        !new Decimal(normalizeDecimal(state)).lt(new Decimal(schema.less_than))
    ) {
        return {
            message: 'errors.validation.lessThan',
            values: { lessThan: schema.less_than },
        }
    }
}

const doubleErrors = (state: any, schema: any): IErrMsg | undefined => {
    if (schema.is_required && !state) {
        return { message: 'errors.validation.fieldRequired' }
    }
    if (String(state).includes('e')) {
        return { message: 'errors.validation.numbersOnly' }
    }
    if (state != null && state > Number.MAX_SAFE_INTEGER) {
        return {
            message: 'errors.validation.greaterThanMaxSafeInt',
        }
    }
    if (state != null && state < Number.MIN_SAFE_INTEGER) {
        return {
            message: 'errors.validation.lessThanMinSafeInt',
        }
    }
    if (
        typeof schema.greater_or_equal !== 'undefined' &&
        Number(state) < schema.greater_or_equal
    ) {
        return {
            message: 'errors.validation.greaterOrEqual',
            values: { greaterOrEqual: schema.greater_or_equal },
        }
    }
    if (
        typeof schema.greater_than !== 'undefined' &&
        Number(state) <= schema.greater_than
    ) {
        return {
            message: 'errors.validation.greaterThan',
            values: { greaterThan: schema.greater_than },
        }
    }
    if (
        typeof schema.less_or_equal !== 'undefined' &&
        Number(state) > schema.less_or_equal
    ) {
        return {
            message: 'errors.validation.lessOrEqual',
            values: { lessOrEqual: schema.less_or_equal },
        }
    }
    if (
        typeof schema.less_than !== 'undefined' &&
        Number(state) >= schema.less_than
    ) {
        return {
            message: 'errors.validation.lessThan',
            values: { lessThan: schema.less_than },
        }
    }
    if (
        typeof schema.precision !== 'undefined' &&
        String(state).split('.')?.[1]?.length >= Number(schema.precision)
    ) {
        return {
            message: 'errors.validation.precision',
            values: { precision: schema.precision },
        }
    }
    if (state) {
        return { message: 'Invalid value' }
    }
}

const integerErrors = (state: any, schema: any): IErrMsg | undefined => {
    if (schema.is_required && !state) {
        return { message: 'errors.validation.fieldRequired' }
    }
    if (String(state).includes('e')) {
        return { message: 'errors.validation.numbersOnly' }
    }

    if (state !== '' && Number.isInteger(Number(state))) {
        if (
            typeof schema.greater_or_equal !== 'undefined' &&
            typeof schema.greater_than !== 'undefined'
        ) {
            const greaterOrEqual = Number(schema.greater_or_equal)
            const greaterThan = Number(schema.greater_than)
            const priorityValidator =
                greaterThan >= greaterOrEqual
                    ? IntegerSchemaValues.GREATER_THAN
                    : IntegerSchemaValues.GREATER_OR_EQUAL

            if (
                priorityValidator === IntegerSchemaValues.GREATER_OR_EQUAL &&
                BigInt(Number(state)) <= BigInt(schema.greater_or_equal)
            ) {
                return {
                    message: 'errors.validation.greaterOrEqual',
                    values: { greaterOrEqual: schema.greater_or_equal },
                }
            }
            if (
                priorityValidator === IntegerSchemaValues.GREATER_THAN &&
                BigInt(Number(state)) <= BigInt(schema.greater_than)
            ) {
                return {
                    message: 'errors.validation.greaterThan',
                    values: { greaterThan: schema.greater_than },
                }
            }
        }

        if (
            typeof schema.greater_or_equal !== 'undefined' &&
            BigInt(Number(state)) <= BigInt(schema.greater_or_equal)
        ) {
            return {
                message: 'errors.validation.greaterOrEqual',
                values: { greaterOrEqual: schema.greater_or_equal },
            }
        }
        if (
            typeof schema.greater_than !== 'undefined' &&
            BigInt(Number(state)) <= BigInt(schema.greater_than)
        ) {
            return {
                message: 'errors.validation.greaterThan',
                values: { greaterThan: schema.greater_than },
            }
        }

        if (
            typeof schema.less_or_equal !== 'undefined' &&
            typeof schema.less_than !== 'undefined'
        ) {
            const lessOrEqual = Number(schema.less_or_equal)
            const lessThan = Number(schema.less_than)
            const priorityValidator =
                lessThan <= lessOrEqual
                    ? IntegerSchemaValues.LESS_THAN
                    : IntegerSchemaValues.LESS_OR_EQUAL

            if (
                priorityValidator === IntegerSchemaValues.LESS_OR_EQUAL &&
                BigInt(Number(state)) > BigInt(schema.less_or_equal)
            ) {
                return {
                    message: 'errors.validation.lessOrEqual',
                    values: { lessOrEqual: schema.less_or_equal },
                }
            }
            if (
                priorityValidator === IntegerSchemaValues.LESS_THAN &&
                BigInt(Number(state)) >= BigInt(schema.less_than)
            ) {
                return {
                    message: 'errors.validation.lessThan',
                    values: { lessThan: schema.less_than },
                }
            }
        }

        if (
            typeof schema.less_or_equal !== 'undefined' &&
            BigInt(Number(state)) > BigInt(schema.less_or_equal)
        ) {
            return {
                message: 'errors.validation.lessOrEqual',
                values: { lessOrEqual: schema.less_or_equal },
            }
        }
        if (
            typeof schema.less_than !== 'undefined' &&
            BigInt(Number(state)) >= BigInt(schema.less_than)
        ) {
            return {
                message: 'errors.validation.lessThan',
                values: { lessThan: schema.less_than },
            }
        }
    }

    if (!Number.isInteger(state)) {
        return { message: 'Must be an integer' }
    }

    if (state) {
        return { message: 'Invalid value' }
    }
}

export const integerRangeErrors = (
    state: any,
    schema: any
): IErrMsg | undefined => {
    if (schema.is_required && !state) {
        return { message: 'errors.validation.fieldRequired' }
    }

    if (typeof state !== 'string') return { message: 'Invalid format' }

    const normalized = state.trim().replace(/\s*-\s*/g, '-')

    const parseRange = (str: string): { values: number[]; error?: IErrMsg } => {
        if (/^\d+$/.test(str)) {
            return { values: [Number(str)] }
        }

        if (/^\d+-\d+$/.test(str)) {
            const [start, end] = str.split('-').map(Number)
            if (start >= end) {
                return {
                    values: [],
                    error: {
                        message: 'errors.validation.rangeEndGreaterThanStart',
                        values: { start: String(start), end: String(end) },
                    },
                }
            }

            const rangeSize = end - start + 1
            if (rangeSize > 10000) {
                return {
                    values: [],
                    error: {
                        message: 'errors.validation.rangeTooLarge',
                        values: {
                            start: String(start),
                            end: String(end),
                            maxRangeSize: '10000',
                        },
                    },
                }
            }

            const range: number[] = []
            for (let i = start; i <= end; i++) range.push(i)
            return { values: range }
        }

        return {
            values: [],
            error: { message: 'errors.validation.invalidRangeFormat' },
        }
    }

    const parsed = parseRange(normalized)

    if (parsed.error) {
        return parsed.error
    }

    const values = parsed.values

    if (String(state).includes('e')) {
        return { message: 'errors.validation.numbersOnly' }
    }

    if (state !== '' && Number.isInteger(Number(state))) {
        if (
            typeof schema.greater_or_equal !== 'undefined' &&
            typeof schema.greater_than !== 'undefined'
        ) {
            const greaterOrEqual = Number(schema.greater_or_equal)
            const greaterThan = Number(schema.greater_than)
            const priorityValidator =
                greaterThan >= greaterOrEqual
                    ? IntegerSchemaValues.GREATER_THAN
                    : IntegerSchemaValues.GREATER_OR_EQUAL

            if (
                priorityValidator === IntegerSchemaValues.GREATER_OR_EQUAL &&
                BigInt(Number(state)) <= BigInt(schema.greater_or_equal)
            ) {
                return {
                    message: 'errors.validation.greaterOrEqual',
                    values: { greaterOrEqual: schema.greater_or_equal },
                }
            }
            if (
                priorityValidator === IntegerSchemaValues.GREATER_THAN &&
                BigInt(Number(state)) <= BigInt(schema.greater_than)
            ) {
                return {
                    message: 'errors.validation.greaterThan',
                    values: { greaterThan: schema.greater_than },
                }
            }
        }

        if (
            typeof schema.greater_or_equal !== 'undefined' &&
            BigInt(Number(state)) <= BigInt(schema.greater_or_equal)
        ) {
            return {
                message: 'errors.validation.greaterOrEqual',
                values: { greaterOrEqual: schema.greater_or_equal },
            }
        }
        if (
            typeof schema.greater_than !== 'undefined' &&
            BigInt(Number(state)) <= BigInt(schema.greater_than)
        ) {
            return {
                message: 'errors.validation.greaterThan',
                values: { greaterThan: schema.greater_than },
            }
        }

        if (
            typeof schema.less_or_equal !== 'undefined' &&
            typeof schema.less_than !== 'undefined'
        ) {
            const lessOrEqual = Number(schema.less_or_equal)
            const lessThan = Number(schema.less_than)
            const priorityValidator =
                lessThan <= lessOrEqual
                    ? IntegerSchemaValues.LESS_THAN
                    : IntegerSchemaValues.LESS_OR_EQUAL

            if (
                priorityValidator === IntegerSchemaValues.LESS_OR_EQUAL &&
                BigInt(Number(state)) > BigInt(schema.less_or_equal)
            ) {
                return {
                    message: 'errors.validation.lessOrEqual',
                    values: { lessOrEqual: schema.less_or_equal },
                }
            }
            if (
                priorityValidator === IntegerSchemaValues.LESS_THAN &&
                BigInt(Number(state)) >= BigInt(schema.less_than)
            ) {
                return {
                    message: 'errors.validation.lessThan',
                    values: { lessThan: schema.less_than },
                }
            }
        }

        if (
            typeof schema.less_or_equal !== 'undefined' &&
            BigInt(Number(state)) > BigInt(schema.less_or_equal)
        ) {
            return {
                message: 'errors.validation.lessOrEqual',
                values: { lessOrEqual: schema.less_or_equal },
            }
        }
        if (
            typeof schema.less_than !== 'undefined' &&
            BigInt(Number(state)) >= BigInt(schema.less_than)
        ) {
            return {
                message: 'errors.validation.lessThan',
                values: { lessThan: schema.less_than },
            }
        }
    }

    if (!values.every(Number.isInteger)) {
        return { message: 'Must be an integer or range of integers' }
    }

    return undefined
}
